// @flow
import { CanonicalTileID } from "./tile_id";
import EXTENT from "../data/extent";
import { RasterBoundsArray } from "../data/array_types";
import { ResourceType, getImage } from "../util/ajax";
import ImageSource from "./image_source";
import { Event, ErrorEvent, Evented } from "../util/evented";
import LngLat from "../geo/lng_lat";
import MercatorCoordinate from "../geo/mercator_coordinate";
import rasterBoundsAttributes from "../data/raster_bounds_attributes";
import SegmentVector from "../data/segment";
import Texture from "../render/texture";
import { extend } from "../util/util";

type Coordinates = [
    [number, number],
    [number, number],
    [number, number],
    [number, number]
];

/**
 * A data source containing an image.
 * (See the [Style Specification](https://www.mapbox.com/mapbox-gl-style-spec/#sources-image) for detailed documentation of options.)
 *
 * @example
 * // add to map
 * map.addSource('some id', {
 *    type: 'image-map',
 *    url: 'http://map.geoq.cn/ArcGIS/rest/services/ChinaOnlineCommunity/MapServer',
 *    rebaseRequestUrl:function(url,params){
 * // 根据不同服务构建url，参考组件层实现
 *   }
 * });
 *
 * // update coordinates
 * var mySource = map.getSource('some id');
 * mySource.setCoordinates([
 *     [-76.54335737228394, 39.18579907229748],
 *     [-76.52803659439087, 39.1838364847587],
 *     [-76.5295386314392, 39.17683392507606],
 *     [-76.54520273208618, 39.17876344106642]
 * ]);
 *
 * // update url and coordinates simultaneously
 * mySource.updateImage({
 *    url: 'https://www.mapbox.com/images/bar.png',
 *    coordinates: [
 *        [-76.54335737228394, 39.18579907229748],
 *        [-76.52803659439087, 39.1838364847587],
 *        [-76.5295386314392, 39.17683392507606],
 *        [-76.54520273208618, 39.17876344106642]
 *    ]
 * })
 *
 * map.removeSource('some id');  // remove
 * @see [Add an image](https://www.mapbox.com/mapbox-gl-js/example/image-on-a-map/)
 */
class ImageMapSource extends ImageSource {
    /**
     * @private
     */
    constructor(
        id: string,
        options:
            | ImageSourceSpecification
            | VideoSourceSpecification
            | CanvasSourceSpecification,
        dispatcher: Dispatcher,
        eventedParent: Evented
    ) {
        super(id, options, dispatcher, eventedParent);
        // 构建图片url方法
        this.rebaseRequestUrl =
            options.rebaseRequestUrl ||
            function (url) {
                return url;
            };
        // 方位点
        this.coordinates = [];
        // 设置source类型，按范围覆盖瓦片
        this.type = "image-map";
        // mapbox-gl-修改说明：新增crs属性
        // 修改人：龚跃健 2024年6月17日
        this.crs = eventedParent.crs;

        this.innerResize = this.innerResize.bind(this);
        this.innnerRotateEnd = this.innnerRotateEnd.bind(this);
        this.innnerPitchEnd = this.innnerPitchEnd.bind(this);
        this.innerMoveEnd = this.innerMoveEnd.bind(this);
    }

    getCoordinates() {
        return this.coordinates;
    }

    onAdd(map) {
        this.map = map;
        map.on("resize", this.innerResize);
        map.on("rotateend", this.innnerRotateEnd);
        map.on("pitchend", this.innnerPitchEnd);
        map.on("moveend", this.innerMoveEnd);
        // 初始化裁剪区
        const options = this._options || this.options;
        if (options) {
            this.setClippingArea(options.clippingArea);
        }
        this.load();
    }

    onRemove(map) {
        map.off("resize", this.innerResize);
        map.off("rotateend", this.innnerRotateEnd);
        map.off("pitchend", this.innnerPitchEnd);
        map.off("moveend", this.innerMoveEnd);
    }

    innerResize() {
        this.loadImage();
    }
    innnerRotateEnd() {
        this.loadImage();
    }
    innnerPitchEnd() {
        this.loadImage();
    }
    innerMoveEnd() {
        this.loadImage();
    }
    loadImage() {
        this.load();
    }

    load(newCoordinates?: Coordinates, successCallback?: () => void) {
        this._loaded = false;
        this.fire(new Event("dataloading", { dataType: "source" }));
        this.url = this.options.url;
        const boxs = newCoordinates || this.transfromMapCoords();
        const wh = this.map._containerDimensions();
        const requestParams =
            this.options && this.options.requestParams
                ? this.options.requestParams
                : {};
        // mapbox-gl-修改说明：image_map_source支持post请求资源
        // 修改人：杨婷茹 2024年5月21日
        // post请求时，解析已被拼接在tileUrl上的bbox和size值
        let tileUrl;
        const isReverse =
            this.url.indexOf("reversebbox=true") >= 0 ? true : false;
        const { bounds, latlngBounds } = this._getImageMapSourceBounds(
            boxs,
            isReverse,
            this.crs
        );
        if (requestParams && requestParams.method === "POST") {
            // 解析出bbx和size的值
            requestParams.data.size = `${wh[0]},${wh[1]}`;
            requestParams.data.bbox = bounds.toString();
            tileUrl = this.url;
        } else {
            tileUrl = this.rebaseRequestUrl(this.url, {
                latlngBounds,
                mercatorBound: bounds,
                imageSize: [wh[0], wh[1]],
            });
        }
        getImage(
            this.map._requestManager.transformRequest(
                tileUrl,
                ResourceType.Image,
                requestParams
            ),
            (err, image) => {
                this._loaded = true;
                if (err) {
                    this.fire(new ErrorEvent(err));
                } else if (image) {
                    this.image = image;
                    if (boxs) {
                        this.coordinates = boxs;
                    }
                    if (successCallback) {
                        successCallback();
                    }
                    this._finishLoading();
                }
            }
        );
    }

    loadTile(tile: Tile, callback: Callback<void>) {
        // We have a single tile -- whoose coordinates are this.tileID -- that
        // covers the image we want to render.  If that's the one being
        // requested, set it up with the image; otherwise, mark the tile as
        // `errored` to indicate that we have no data for it.
        // If the world wraps, we may have multiple "wrapped" copies of the
        // single tile.
        if (this.tileID && this.tileID.equals(tile.tileID.canonical)) {
            this.tiles[String(tile.tileID.wrap)] = tile;
            // 设置在parentTile时，不发生瓦片渲染的渐变
            // 此处在底层program.js draw函数中被处理，原理修改顶点透明度
            tile.refreshedUponExpiration = true;
            tile.buckets = {};
            callback(null);
        } else {
            tile.state = "errored";
            callback(null);
        }
    }

    transfromMapCoords() {
        const bounds = this.map.getBounds();
        // toArray= [[-73.9876, 40.7661], [-73.9397, 40.8002]]
        const arr = bounds.toArray();
        return [
            [arr[0][0], arr[1][1]],
            [arr[1][0], arr[1][1]],
            [arr[1][0], arr[0][1]],
            [arr[0][0], arr[0][1]],
        ];
    }

    transfromCoordsToBound(newCoordinates) {
        const bx = [],
            by = [];
        newCoordinates.forEach((coord) => {
            bx.push(coord[0]);
            by.push(coord[1]);
        });
        return [
            Math.min(...bx),
            Math.min(...by),
            Math.max(...bx),
            Math.max(...by),
        ];
    }

    /**
     * @description 获取影像地理范围和投影坐标系范围
     * @param {Coordinates} coordinates
     * @param {Boolean} isReverse
     * @param {CRS} crs
     * @return {*}
     */
    _getImageMapSourceBounds(newCoordinates?: Coordinates, reverse, crs) {
        const latlngBounds = this.transfromCoordsToBound(newCoordinates);
        const x1 = crs.projection.project(
            new LngLat(latlngBounds[0], latlngBounds[1])
        );
        const x2 = crs.projection.project(
            new LngLat(latlngBounds[2], latlngBounds[3])
        );
        const bounds = [x1[0], x1[1], x2[0], x2[1]];
        if (reverse) {
            [latlngBounds, bounds].forEach((b) => {
                const [x1, y1, x2, y2] = b;
                b[0] = y1;
                b[1] = x1;
                b[2] = y2;
                b[3] = x2;
            });
        }
        return {
            bounds,
            latlngBounds,
        };
    }

    _finishLoading() {
        if (this.map) {
            this.setCoordinates(this.coordinates);
            this.fire(
                new Event("data", {
                    dataType: "source",
                    sourceDataType: "metadata",
                })
            );
        }
    }

    updateImage(options: { url: string, coordinates?: Coordinates }) {
        if (!this.image || !options.url) {
            return this;
        }
        this.options.url = options.url;
        this.load(this.coordinates, () => {
            this.texture = null;
        });
        return this;
    }

    prepare() {
        if (Object.keys(this.tiles).length === 0 || !this.image) {
            return;
        }
        const context = this.map.painter.context;
        const gl = context.gl;

        if (!this.boundsBuffer) {
            this.boundsBuffer = context.createVertexBuffer(
                this._boundsArray,
                rasterBoundsAttributes.members
            );
        }

        if (!this.boundsSegments) {
            this.boundsSegments = SegmentVector.simpleSegment(0, 0, 4, 2);
        }

        if (!this.texture) {
            this.texture = new Texture(context, this.image, gl.RGBA);
            this.texture.bind(gl.LINEAR, gl.CLAMP_TO_EDGE);
        }
        // 更新图元
        this.texture.update(this.image);

        for (const w in this.tiles) {
            const tile = this.tiles[w];
            if (tile.state !== "loaded") {
                tile.state = "loaded";
                tile.texture = this.texture;
            }
        }
    }
    // mapbox-gl-修改说明：解决source序列化后再次加载时类型错误问题，以及coordinates为空数组导致的类型验证报错问题
    // 修改人：赵凯 2024年9月24日
    serialize(): Object {
        return extend({}, this.options, {
            type: this.type,
            coordinates: this.transfromMapCoords(),
        });
    }
}

export default ImageMapSource;
